import { COST_BANDS, LEGACY_COST_BANDS, costBandLabel } from './cost-bands';
import type { LikertLevel, CryptoAgility, Upgradability } from '../api/types';

/**
 * The 5-level Likert scale used across most categorical risk parameters.
 * Pairs the machine-readable code with the human-readable display label
 * (matches the legacy product's labels — "Very Low" not "Very_low").
 */
export const LIKERT_OPTIONS: Array<{ code: LikertLevel; label: string }> = [
  { code: 'Very_low',  label: 'Very Low' },
  { code: 'Low',       label: 'Low' },
  { code: 'Moderate',  label: 'Moderate' },
  { code: 'High',      label: 'High' },
  { code: 'Very_high', label: 'Very High' },
];

export const CRYPTO_AGILITY_OPTIONS: Array<{ code: CryptoAgility; label: string }> = [
  { code: 'L0_Initial',    label: 'L0: Initial' },
  { code: 'L1_Developing', label: 'L1: Developing' },
  { code: 'L2_Defined',    label: 'L2: Defined' },
  { code: 'L3_Managed',    label: 'L3: Managed' },
  { code: 'L4_Optimized',  label: 'L4: Optimized' },
];

export const UPGRADABILITY_OPTIONS: Array<{ code: Upgradability; label: string }> = [
  { code: 'Very_easy',      label: 'Very Easy' },
  { code: 'Easy',           label: 'Easy' },
  { code: 'Moderate',       label: 'Moderate' },
  { code: 'Difficult',      label: 'Difficult' },
  { code: 'Very_difficult', label: 'Very Difficult' },
];

/**
 * Migration cost bands (lib/cost-bands.ts): amounts in the project currency, labelled in it
 * ("₹1L–2.5L", "USD 10k–25k"). The five older bands are offered only to an asset that still holds
 * one, marked "(old band)", so its value still shows.
 */
export function migrationCostOptionsFor(current: string | null | undefined, currency: string | null = null): Array<{ code: string; label: string; legacy?: boolean }> {
  return [
    ...COST_BANDS.map((code) => ({ code, label: costBandLabel(code, currency) })),
    ...LEGACY_COST_BANDS.filter((c) => c === current).map((code) => ({ code, label: `${costBandLabel(code, currency)} (old band)`, legacy: true })),
  ];
}

/**
 * Stable parameter UUIDs from V013 and V015 seeds. The form writes
 * captured parameter values keyed by these IDs. When the parameter
 * catalog grows beyond Slice 6a, replace this with a metadata fetch.
 */
export const PARAM_IDS = {
  regulatory_requirements_level: '00000000-0000-0000-5001-000000000016',
  migration_cost_range:          '00000000-0000-0000-5001-000000000060',
  complexity:                    '00000000-0000-0000-5001-000000000061',
  feasibility:                   '00000000-0000-0000-5001-000000000062',
} as const;

/**
 * All UN-recognised sovereign states and commonly used territories,
 * stored as full display names. Sorted alphabetically.
 * Used in the company creation form country dropdown.
 */
export const COUNTRY_OPTIONS: string[] = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola',
  'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
  'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
  'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile',
  'China', 'Colombia', 'Comoros', 'Congo (Brazzaville)', 'Congo (Kinshasa)',
  'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador',
  'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau',
  'Guyana', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar',
  'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan',
  'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay',
  'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
  'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia',
  'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan',
  'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan',
  'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
  'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City',
  'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];
